import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Factory, Scissors, Tag, ArrowRight } from "lucide-react";
import { Seo } from "@/components/Seo";
import { ProductCard } from "@/components/ProductCard";
import { IMAGES, PRODUCTS, CATEGORIES } from "@/data/products";

const fade = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: "easeOut" },
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Shineni India — B2B Denim Jeans Manufacturer, Faridabad"
        description="In-house denim jeans manufacturing in Faridabad, Haryana. Wholesale supply across India, international export and private-label programs. Made in India."
      />

      {/* Hero */}
      <section className="relative flex min-h-[88vh] items-center" data-testid="home-hero">
        <img src={IMAGES.factory} alt="Denim garment factory floor with sewing lines" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-indigo-deep/70" />
        <div className="relative mx-auto w-full max-w-7xl px-4 py-24 sm:px-6">
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">
            Denim Manufacturing · Faridabad, India
          </motion.p>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }}
            className="mt-5 max-w-3xl font-display text-4xl font-semibold leading-tight text-white sm:text-5xl lg:text-6xl">
            Premium denim jeans, made under one roof.
          </motion.h1>
          <motion.p initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.2 }}
            className="mt-6 max-w-xl text-base leading-relaxed text-white/80 sm:text-lg">
            Shineni India manufactures jeans for men, women and kids — with direct factory
            pricing and flexible private-label programs for wholesalers, importers and fashion brands.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.3 }}
            className="mt-10 flex flex-wrap gap-4">
            <Link to="/request-a-quote" data-testid="hero-quote-cta"
              className="rounded-sm bg-gold px-8 py-3.5 text-sm font-semibold text-indigo-deep transition-colors duration-200 hover:bg-gold-dark">
              Request a Quote
            </Link>
            <Link to="/capabilities" data-testid="hero-capabilities-cta"
              className="rounded-sm border border-white/40 px-8 py-3.5 text-sm font-semibold text-white transition-colors duration-200 hover:bg-white/10">
              Explore Capabilities
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Positioning strip */}
      <section className="border-b border-border bg-white">
        <div className="mx-auto grid max-w-7xl grid-cols-2 gap-6 px-4 py-8 text-center sm:px-6 lg:grid-cols-4">
          {["Made in India", "Faridabad, Haryana", "B2B & Private Label", "Domestic + Export"].map((t) => (
            <p key={t} className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo">{t}</p>
          ))}
        </div>
      </section>

      {/* Product ranges */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24" data-testid="home-ranges">
        <motion.div {...fade} className="max-w-2xl">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">What we manufacture</p>
          <h2 className="mt-3 font-display text-3xl font-semibold text-indigo sm:text-4xl">Denim for every buyer program</h2>
          <p className="mt-4 text-base text-foreground/70">
            Sample styles below show the ranges we produce. Every order is built to your tech pack —
            fabric, fit, wash, trims and labelling. All imagery is representative sample imagery.
          </p>
        </motion.div>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {CATEGORIES.map((c) => {
            const p = PRODUCTS.find((x) => x.category === c.id);
            return (
              <motion.div key={c.id} {...fade}>
                <ProductCard product={p} />
              </motion.div>
            );
          })}
        </div>
        <motion.div {...fade} className="mt-10">
          <Link to="/products" data-testid="home-catalogue-link"
            className="inline-flex items-center gap-2 border-b-2 border-gold pb-1 text-sm font-semibold text-indigo transition-colors hover:text-gold-dark">
            View the full sample catalogue <ArrowRight className="h-4 w-4" />
          </Link>
        </motion.div>
      </section>

      {/* Why Shineni */}
      <section className="bg-indigo py-20 text-white sm:py-24" data-testid="home-why">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <motion.div {...fade} className="max-w-2xl">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">Why buyers choose us</p>
            <h2 className="mt-3 font-display text-3xl font-semibold sm:text-4xl">A factory partner, not a middleman</h2>
          </motion.div>
          <div className="mt-12 grid gap-px overflow-hidden rounded-sm bg-white/10 sm:grid-cols-3">
            {[
              { icon: Factory, title: "Direct factory pricing", text: "You deal with the manufacturer in Faridabad — no traders, no layered margins." },
              { icon: Scissors, title: "Everything under one roof", text: "Pattern, cutting, stitching, washing and finishing handled in-house for consistent quality." },
              { icon: Tag, title: "Flexible private label", text: "Your brand, your tech pack, your labels — from sampling to bulk production." },
            ].map((f) => (
              <motion.div key={f.title} {...fade} className="bg-indigo p-8 sm:p-10">
                <f.icon className="h-7 w-7 text-gold" />
                <h3 className="mt-5 font-display text-xl font-semibold">{f.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-white/70">{f.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Wholesale / Export split */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 sm:py-24" data-testid="home-channels">
        <div className="grid gap-6 lg:grid-cols-2">
          <motion.div {...fade} className="border border-border bg-white p-8 sm:p-12">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">Within India</p>
            <h2 className="mt-3 font-display text-2xl font-semibold text-indigo sm:text-3xl">Wholesale supply across India</h2>
            <p className="mt-4 text-sm leading-relaxed text-foreground/70">
              For wholesalers, distributors and retail chains looking for reliable denim supply at
              factory-direct terms. Tell us your ranges and quantities.
            </p>
            <Link to="/indian-wholesale" data-testid="home-wholesale-cta"
              className="mt-8 inline-flex items-center gap-2 rounded-sm bg-indigo px-6 py-3 text-sm font-semibold text-white transition-colors hover:bg-indigo-deep">
              Wholesale enquiry <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>
          <motion.div {...fade} className="relative overflow-hidden border border-border p-8 text-white sm:p-12">
            <img src={IMAGES.texture} alt="Indigo denim twill weave close-up" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-indigo-deep/75" />
            <div className="relative">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-gold">Worldwide</p>
              <h2 className="mt-3 font-display text-2xl font-semibold sm:text-3xl">International export</h2>
              <p className="mt-4 text-sm leading-relaxed text-white/80">
                For importers, buying houses and fashion brands sourcing Made-in-India denim.
                Share your destination and specifications to start the conversation.
              </p>
              <Link to="/international-export" data-testid="home-export-cta"
                className="mt-8 inline-flex items-center gap-2 rounded-sm bg-gold px-6 py-3 text-sm font-semibold text-indigo-deep transition-colors hover:bg-gold-dark">
                Export enquiry <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* CTA band */}
      <section className="relative py-20 sm:py-24" data-testid="home-cta-band">
        <img src={IMAGES.folded} alt="Stack of folded denim jeans ready for dispatch" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-indigo-deep/80" />
        <div className="relative mx-auto max-w-3xl px-4 text-center sm:px-6">
          <motion.h2 {...fade} className="font-display text-3xl font-semibold text-white sm:text-4xl">
            Have a denim requirement? Let's talk quantities.
          </motion.h2>
          <motion.p {...fade} className="mt-4 text-white/75">
            Send your specifications and get a response from the factory — not a sales agent.
          </motion.p>
          <motion.div {...fade} className="mt-8 flex flex-wrap justify-center gap-4">
            <Link to="/request-a-quote" data-testid="cta-band-quote"
              className="rounded-sm bg-gold px-8 py-3.5 text-sm font-semibold text-indigo-deep transition-colors hover:bg-gold-dark">
              Request a Quote
            </Link>
            <Link to="/contact" data-testid="cta-band-contact"
              className="rounded-sm border border-white/40 px-8 py-3.5 text-sm font-semibold text-white transition-colors hover:bg-white/10">
              Contact Us
            </Link>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
